/**
 * Checkout initial state and constants. Used by useCheckout.
 */
import {
  getShopType,
  SHOP_TYPES,
  CHECKOUT_PANELS as PANELS,
} from "../components/shop/helpers";
import type {
  CheckoutProps,
  CheckoutState,
  ContactEntity,
} from "../components/shop/Checkout/types";

const defaultPanelState = {
  showCheckmark: false,
  showEditBtn: false,
  showPreview: false,
  showErrors: false,
  opened: false,
};

export const EDIT_CHECKOUT_OMIT_KEYS = [
  "cart",
  "shop",
  "orderTotal",
  "focus",
  "avalara_summary",
  "code",
  "applied_discount",
] as const;

export type PublicViewContextValue = {
  publicViewEntity?: {
    entityAggregate?: number;
    entityClientId?: string;
    entityType?: string;
    entity?: Record<string, unknown> & { billing_address_type?: string };
    contact?: ContactEntity;
    entityOrder?: unknown;
    entityId?: string;
    entityForceMinimumQty?: number;
    entityIsFree?: number;
    perCheckoutLimit?: number;
    buyInventory?: unknown;
    entityLocation?: string;
  };
  onClickCart?: () => void;
};

export function getInitialCheckoutState(
  props: CheckoutProps,
  context: PublicViewContextValue | undefined,
): CheckoutState {
  const aggregate =
    context?.publicViewEntity?.entityAggregate ??
    (props as unknown as { aggregate?: number })?.aggregate;
  const clientId =
    context?.publicViewEntity?.entityClientId ??
    (props as unknown as { clientId?: string })?.clientId;
  const publicEntity = context?.publicViewEntity as
    | { contact?: ContactEntity; entity?: Record<string, unknown> }
    | undefined;
  const contact = publicEntity?.contact ?? (props as CheckoutProps).contact;
  const hasClient =
    clientId ||
    (contact &&
      typeof contact === "object" &&
      (contact as ContactEntity).company_id);
  const prefill =
    contact &&
    typeof contact === "object" &&
    (contact as ContactEntity).contact_first_name;
  const entityType =
    context?.publicViewEntity?.entityType ??
    (props as CheckoutProps).entityType;
  const entityForBilling = context?.publicViewEntity?.entity as
    | Record<string, string>
    | undefined;
  const billingAddressType =
    entityForBilling?.billing_address_type ??
    (props as unknown as { billingAddressType?: string })?.billingAddressType;
  const defaultPanel = !(!!clientId || "TYPED" !== billingAddressType)
    ? PANELS.COMPANY_NAME
    : getShopType({
          aggregate,
          client_id: clientId,
        } as Parameters<typeof getShopType>[0]) === SHOP_TYPES.COMPANY_SHOP
      ? PANELS.BILLING_INFO
      : PANELS.CUSTOMER_INFO;

  const contactObj =
    contact && typeof contact === "object" ? (contact as ContactEntity) : null;
  const companyName = contactObj?.company_name ?? "";

  return {
    focus: null,
    sameAsBilling: false,
    billing_address__validated: false,
    shipping_address__validated: false,
    billing_address: {
      address_company: companyName,
      address_line_1: "",
      address_line_2: "",
      address_city: "",
      address_state: "",
      address_postal: "",
      address_country: "",
    },
    shipping_address: {
      address_company: companyName,
      address_line_1: "",
      address_line_2: "",
      address_city: "",
      address_state: "",
      address_postal: "",
      address_country: "",
    },
    billing_contact: {
      contact_name: prefill
        ? `${contactObj?.contact_first_name ?? ""} ${
            contactObj?.contact_last_name ?? ""
          }`.trim()
        : "",
      contact_email: contactObj?.contact_email ?? "",
    },
    shipping_contact: {
      contact_name: prefill
        ? `${contactObj?.contact_first_name ?? ""} ${
            contactObj?.contact_last_name ?? ""
          }`.trim()
        : "",
      contact_email: contactObj?.contact_email ?? "",
    },
    errors: {},
    comment: "",
    shipping_cost: 0,
    shipping_range_id: null,
    order_contact: { contact_name: "", contact_email: "" },
    order_metadata: {},
    cart: props.cart,
    orderTotal: props.orderTotal,
    code: "",
    show_coupon_apply: false,
    applied_discount: null,
    customer_po: "",
    avalara_summary: false,
    showPodValidationModal: false,
    openPanel: defaultPanel,
    PANEL_COMPANY_NAME: { ...defaultPanelState },
    PANEL_SHIPPING_INFO: { ...defaultPanelState },
    PANEL_BILLING_INFO: { ...defaultPanelState },
    PANEL_CUSTOMER_INFO: { ...defaultPanelState },
    PANEL_ADDITIONAL_INFO: { ...defaultPanelState },
    PANEL_COMMENTS: { ...defaultPanelState },
    billing_address_id:
      hasClient &&
      props.addresses.filter((a) => a.address_type === "BILLING")[0]
        ? (props as unknown as { billingAddressType?: string })
            .billingAddressType === "TYPED"
          ? "ADD"
          : ""
        : "ADD",
    shipping_address_id:
      hasClient &&
      props.addresses.filter((a) => a.address_type === "SHIPPING")[0]
        ? (props as unknown as { shippingAddressType?: string })
            .shippingAddressType === "TYPED"
          ? "ADD"
          : ""
        : "ADD",
    billing_contact_id:
      hasClient &&
      props.contacts.filter(
        (c) =>
          [entityType, `${entityType}-BILLING`].indexOf(
            c.company_type ?? "",
          ) !== -1,
      )[0]
        ? (props as unknown as { billingContactType?: string })
            .billingContactType === "TYPED"
          ? "ADD"
          : ""
        : "ADD",
    shipping_contact_id:
      hasClient &&
      props.contacts.filter(
        (c) =>
          [entityType, `${entityType}-SHIPPING`].indexOf(
            c.company_type ?? "",
          ) !== -1,
      )[0]
        ? (props as unknown as { shippingContactType?: string })
            .shippingContactType === "TYPED"
          ? "ADD"
          : ""
        : "ADD",
    ...(props.checkout as Partial<CheckoutState>),
  } as CheckoutState;
}
